import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const timestampPattern = /@(\d+:\d{2}(?::\d{2})?)@/g;

function renderValue(container, value) {
    container.replaceChildren();

    let lastIndex = 0;
    let match;

    timestampPattern.lastIndex = 0;

    while ((match = timestampPattern.exec(value)) !== null) {
        if (match.index > lastIndex) {
            container.appendChild(
                document.createTextNode(value.slice(lastIndex, match.index)),
            );
        }

        const timestamp = document.createElement("span");
        timestamp.dataset.commentTimestamp = match[1];
        timestamp.contentEditable = "false";
        timestamp.setAttribute("aria-label", `Video timestamp ${match[1]}`);
        timestamp.textContent = match[1];
        container.appendChild(timestamp);

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < value.length) {
        container.appendChild(document.createTextNode(value.slice(lastIndex)));
    }
}

function serializeNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.nodeValue || "";
    }

    if (node.nodeName === "BR") {
        return "\n";
    }

    if (node instanceof HTMLElement && node.dataset.commentTimestamp) {
        return `@${node.dataset.commentTimestamp}@`;
    }

    return Array.from(node.childNodes).map(serializeNode).join("");
}

export default function TimestampCommentField({
    value,
    onChange,
    onFocus,
    onSubmit,
    onCancel,
    onTimestampClick,
    placeholder,
    label,
    minHeight = 72,
    disabled = false,
    autoFocus = false,
}) {
    const editorRef = useRef(null);
    const lastValueRef = useRef(value);

    useEffect(() => {
        const editor = editorRef.current;

        if (!editor || value === lastValueRef.current) {
            return;
        }

        renderValue(editor, value);
        lastValueRef.current = value;
    }, [value]);

    useEffect(() => {
        const editor = editorRef.current;

        if (!editor) {
            return;
        }

        renderValue(editor, value);
        lastValueRef.current = value;

        if (autoFocus) {
            editor.focus();
        }
        // This sets the initial DOM for the controlled content-editable field.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function emitChange() {
        if (!editorRef.current) {
            return;
        }

        const nextValue = serializeNode(editorRef.current);
        lastValueRef.current = nextValue;
        onChange(nextValue);
    }

    function insertPlainText(text) {
        document.execCommand("insertText", false, text);
        emitChange();
    }

    function handleKeyDown(event) {
        if (
            event.key === "Enter" &&
            (event.ctrlKey || event.metaKey) &&
            onSubmit
        ) {
            event.preventDefault();
            onSubmit();
            return;
        }

        if (event.key === "Escape" && onCancel) {
            event.preventDefault();
            onCancel();
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();
            insertPlainText("\n");
        }
    }

    function handlePaste(event) {
        event.preventDefault();
        insertPlainText(event.clipboardData.getData("text/plain"));
    }

    function handleClick(event) {
        const timestamp = event.target.closest?.("[data-comment-timestamp]");

        if (timestamp && onTimestampClick) {
            onTimestampClick(timestamp.dataset.commentTimestamp);
        }
    }

    return (
        <Box sx={{ width: "100%" }}>
            {label && (
                <Typography
                    component="label"
                    variant="caption"
                    sx={{
                        display: "block",
                        mb: 0.5,
                        color: "text.secondary",
                        fontWeight: 700,
                    }}
                >
                    {label}
                </Typography>
            )}
            <Box
                ref={editorRef}
                role="textbox"
                aria-label={label || placeholder}
                aria-multiline="true"
                contentEditable={!disabled}
                suppressContentEditableWarning
                data-placeholder={placeholder}
                onInput={emitChange}
                onFocus={onFocus}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onClick={handleClick}
                sx={{
                    minHeight,
                    maxHeight: 190,
                    overflowY: "auto",
                    px: 1.5,
                    py: 1.15,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                    backgroundColor: disabled
                        ? "action.disabledBackground"
                        : "background.paper",
                    color: disabled ? "text.disabled" : "text.primary",
                    fontSize: "0.875rem",
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    outline: "none",
                    transition:
                        "border-color 150ms ease, box-shadow 150ms ease",
                    "&:hover": {
                        borderColor: disabled
                            ? "rgba(0, 0, 0, 0.23)"
                            : "text.primary",
                    },
                    "&:focus": {
                        borderColor: "primary.main",
                        boxShadow: "0 0 0 1px rgb(0, 149, 61)",
                    },
                    "&:empty::before": {
                        content: "attr(data-placeholder)",
                        color: "text.disabled",
                        pointerEvents: "none",
                    },
                    "& [data-comment-timestamp]": {
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.45,
                        mx: 0.3,
                        px: 0.8,
                        py: 0.05,
                        borderRadius: 4,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                        fontWeight: 700,
                        lineHeight: 1.65,
                        cursor: onTimestampClick ? "pointer" : "default",
                        userSelect: "all",
                    },
                    "& [data-comment-timestamp]::before": {
                        content: '"▶"',
                        fontSize: "0.72em",
                    },
                }}
            />
        </Box>
    );
}
